import React, { useCallback, useEffect, useRef, useState } from "react";
import OrbitalLoader from "../common/OrbitalLoader";
import IdeaQueueCard from "./IdeaQueueCard";

const EXIT_CLASSES = {
  convert: "translate-x-[-130%] -rotate-6 opacity-0 scale-95",
  reject: "translate-x-[130%] rotate-6 opacity-0 scale-95",
  skip: "translate-y-16 scale-90 opacity-0",
};

const IdeaQueueDeck = ({
  ideas,
  totalCount,
  reviewedAll,
  loading,
  error,
  onGenerate,
  onCreateScript,
  onReject,
  onReviewAgain,
  isGeneratingScript = false,
  exitTrigger = null,
  onExitSettled,
}) => {
  const [orderedIdeas, setOrderedIdeas] = useState([]);
  const [exitMode, setExitMode] = useState(null);
  const [animatingHash, setAnimatingHash] = useState(null);
  const lastExitKeyRef = useRef(null);

  useEffect(() => {
    if (!exitMode) {
      setOrderedIdeas(Array.isArray(ideas) ? ideas : []);
    }
  }, [ideas, exitMode]);

  const runExit = useCallback((mode, idea, afterExit) => {
    if (!idea || exitMode) return;
    setExitMode(mode);
    setAnimatingHash(idea.hash);
    window.setTimeout(() => {
      afterExit?.(idea);
      onExitSettled?.(idea.hash, mode);
      setExitMode(null);
      setAnimatingHash(null);
    }, 420);
  }, [exitMode, onExitSettled]);

  useEffect(() => {
    if (!exitTrigger?.hash || !exitTrigger?.mode || !exitTrigger?.key) return;
    if (lastExitKeyRef.current === exitTrigger.key) return;
    const topIdea = orderedIdeas[0];
    if (!topIdea || topIdea.hash !== exitTrigger.hash || exitMode) return;
    lastExitKeyRef.current = exitTrigger.key;
    runExit(exitTrigger.mode, topIdea);
  }, [exitTrigger, orderedIdeas, exitMode, runExit]);

  const handleSkip = (idea) => {
    runExit("skip", idea);
  };

  const topIdea = orderedIdeas[0] || null;
  const backIdeas = orderedIdeas.slice(1, 3);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-theme-text">
          Video idea queue{" "}
          {!loading ? (
            <span className="text-theme-text opacity-50">({totalCount ?? orderedIdeas.length})</span>
          ) : null}
        </h2>
      </div>

      {error ? (
        <div className="rounded-xl border border-theme-secondary bg-theme-secondary p-4 text-sm text-theme-text opacity-80">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-16">
          <OrbitalLoader />
        </div>
      ) : !topIdea ? (
        <div className="rounded-2xl border border-dashed border-theme-secondary bg-theme-secondary/60 p-10 text-center">
          <p className="text-base font-medium text-theme-text">
            {reviewedAll ? "You reviewed all queued ideas" : "Your queue is empty"}
          </p>
          <p className="text-sm text-theme-text opacity-70 mt-2 max-w-md mx-auto">
            {reviewedAll
              ? "Start over to revisit the ideas you skipped."
              : "Generate a fresh batch of ideas tailored to your brand and top-performing patterns."}
          </p>
          <button
            type="button"
            onClick={reviewedAll ? onReviewAgain : onGenerate}
            className="mt-5 text-sm font-medium text-theme-primary hover:opacity-80"
          >
            {reviewedAll ? "Review again" : "Generate ideas"}
          </button>
        </div>
      ) : (
        <div className="relative mx-auto max-w-3xl min-w-0 px-5 pt-3 pb-10">
          <div className="relative grid min-w-0">
            {backIdeas.map((idea, index) => (
              <div
                key={idea.hash}
                aria-hidden
                className={`col-start-1 row-start-1 rounded-2xl border border-theme-shadow bg-theme-secondary shadow-lg pointer-events-none transition-transform duration-300 ${
                  index === 0
                    ? "translate-y-3 -translate-x-3 -rotate-2 scale-[0.98]"
                    : "translate-y-5 translate-x-3 rotate-3 scale-[0.96]"
                }`}
                style={{ zIndex: 10 - index, opacity: 0.8 - index * 0.15 }}
              />
            ))}

            <div
              className={`col-start-1 row-start-1 relative z-20 min-w-0 transition-all duration-500 ease-out motion-reduce:transition-none ${
                animatingHash === topIdea.hash && exitMode ? EXIT_CLASSES[exitMode] : ""
              }`}
            >
              <IdeaQueueCard
                idea={topIdea}
                onCreateScript={(idea) => onCreateScript?.(idea)}
                onReject={onReject}
                onSkip={handleSkip}
                isGeneratingScript={isGeneratingScript}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdeaQueueDeck;
